import { motion } from 'motion/react';
import { useEffect, useState } from 'react';

import { ApiError, api } from '../api/client';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { fadeUp } from '../lib/motionPresets';
import { useSession } from '../lib/SessionContext';

/**
 * LLMR-61's consent switch, on the Billing page. Read is open to every role
 * (GET /quota/auto-fallback has no role gate, same shape as balanceStatus);
 * only owner/admin can change it (`canManageBilling`, same tier
 * member-api's canManageMembers uses for the PATCH).
 *
 * Chips, not a dedicated switch component — same "pick one of two options"
 * visual language TopUp.tsx already uses for its amount presets on this
 * exact page, rather than introducing a new control style for one setting.
 */
export function AutoFallbackToggle() {
  const { canManageBilling, isB2BOrg } = useSession();
  const { dict } = useLanguage();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await api.autoFallback();
        if (!cancelled) setEnabled(r.enabled);
      } catch {
        if (!cancelled) setError(dict.quota.loadError);
      }
    })();
    return () => {
      cancelled = true;
    };
    // dict is stable per locale; re-running on every dict identity change
    // would refetch on a language switch for no reason, so only the message
    // text itself is read fresh at error time, not tracked as a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function choose(next: boolean) {
    if (enabled === next || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.setAutoFallback(next);
      setEnabled(r.enabled);
    } catch (e) {
      setError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.quota.saveError);
    } finally {
      setBusy(false);
    }
  }

  // Nothing to show yet, and nothing to show forever if the very first fetch
  // failed silently for an org with no organisation at all — TopUp.tsx's own
  // onboarding gate already covers that case above this component.
  if (enabled === null && !error) return null;

  return (
    <motion.section variants={fadeUp} initial="hidden" animate="show" className="panel billing-panel">
      <h2>{dict.quota.title}</h2>
      <p className="note">{dict.quota.description}</p>

      {error && <div className="err">{error}</div>}

      {canManageBilling ? (
        <div className="amount-presets">
          <button
            type="button"
            className={`chip${enabled === true ? ' chip--active' : ''}`}
            disabled={busy}
            onClick={() => void choose(true)}
          >
            {dict.quota.enabledLabel}
          </button>
          <button
            type="button"
            className={`chip${enabled === false ? ' chip--active' : ''}`}
            disabled={busy}
            onClick={() => void choose(false)}
          >
            {dict.quota.disabledLabel}
          </button>
        </div>
      ) : (
        <>
          <p className="note">{dict.quota.restricted(isB2BOrg)}</p>
          {enabled !== null && (
            <span className={`badge badge--${enabled ? 'active' : 'pending'}`}>
              {enabled ? dict.quota.enabledLabel : dict.quota.disabledLabel}
            </span>
          )}
        </>
      )}

      {busy && (
        <p className="sub" role="status">
          {dict.quota.saving}
        </p>
      )}
    </motion.section>
  );
}
